import api from '../utils/api';
import { normalizeApiResponse } from '../utils/apiResponseHandler';

// User service for API calls
export const userService = {
  // Get users with pagination, search, and role filtering
  getUsers: async (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = '',
      role = '',
      is_active = '',  // Changed from 'status' to 'is_active'
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('per_page', per_page);
      
      if (search) queryParams.append('search', search);
      if (role) queryParams.append('role', role);
      if (is_active !== '') queryParams.append('is_active', is_active);  // Changed to use 'is_active'
      if (sort_by) queryParams.append('sort_by', sort_by);
      if (sort_order) queryParams.append('sort_order', sort_order);

      const response = await api.get(`/admin/users?${queryParams.toString()}`);
      return normalizeApiResponse(response, 'users');
    } catch (error) {
      throw error;
    }
  },

  // Get single user by ID
  getUser: async (userId) => {
    try {
      const response = await api.get(`/admin/users/${userId}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Create new user
  createUser: async (userData) => {
    try {
      const response = await api.post('/admin/users', userData);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update user
  updateUser: async (userId, userData) => {
    try {
      const response = await api.put(`/admin/users/${userId}`, userData);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Delete user
  deleteUser: async (userId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Bulk delete users
  bulkDeleteUsers: async (userIds) => {
    try {
      const response = await api.post('/admin/users/bulk-delete', { user_ids: userIds });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Update user status
  updateUserStatus: async (userId, status) => {
    try {
      const response = await api.patch(`/admin/users/${userId}/status`, { status });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Assign role to user
  assignRole: async (userId, roleId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/roles`, { role_id: roleId });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Remove role from user
  removeRole: async (userId, roleId) => {
    try {
      const response = await api.delete(`/admin/users/${userId}/roles/${roleId}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },


  // Get user activity log
  getUserActivity: async (userId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await api.get(`/admin/users/${userId}/activity?${queryParams.toString()}`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Export users
  exportUsers: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await api.get(`/admin/users/export?${queryParams.toString()}`, {
        responseType: 'blob'
      });
      // Export returns blob data, no need to normalize
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Toggle user status (active/inactive)
  toggleUserStatus: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/toggle-status`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Login as user (impersonation)
  loginAsUser: async (userId) => {
    try {
      const response = await api.post(`/admin/users/${userId}/login-as`);
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Return to super admin from impersonation
  returnToAdmin: async () => {
    try {
      const response = await api.post('/users/return-to-admin');
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Change user subscription plan
  changeUserPlan: async (userId, pricingPlanId) => {
    try {
      const response = await api.patch(`/admin/users/${userId}/subscription/plan`, {
        pricing_plan_id: pricingPlanId
      });
      return normalizeApiResponse(response);
    } catch (error) {
      throw error;
    }
  }
};

export default userService;